import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, Button, Space, Tag, Typography, Input, Popconfirm, message, Modal, Select, Row, Col } from 'antd';
import { SearchOutlined, DeleteOutlined, EyeOutlined, ReloadOutlined, CalendarOutlined, ClockCircleOutlined, CheckCircleOutlined, DollarOutlined } from '@ant-design/icons';
import StatCard from '../components/StatCard';
import useBookingStore from '../store/bookingStore';

const { Title } = Typography;

const statusColors = {
  pending: 'orange',
  confirmed: 'blue',
  cancelled: 'red',
  completed: 'green',
};

const statusLabels = {
  pending: 'Kutilmoqda',
  confirmed: 'Tasdiqlangan',
  cancelled: 'Bekor qilingan',
  completed: 'Yakunlangan',
};

const BookingsPage = () => {
  const { bookings, loading, pagination, fetchBookings, deleteBooking } = useBookingStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleDelete = async (id) => {
    try {
      await deleteBooking(id);
      message.success("O'chirildi");
      fetchBookings();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: (a, b) => a.id - b.id },
    { title: 'Xizmat ID', dataIndex: 'service_id', key: 'service_id' },
    { title: 'Foydalanuvchi ID', dataIndex: 'user_id', key: 'user_id' },
    {
      title: 'Narxi',
      dataIndex: 'price',
      key: 'price',
      render: (v) => `${Number(v || 0).toLocaleString()} UZS`,
    },
    {
      title: 'Jami',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (v) => `${Number(v || 0).toLocaleString()} UZS`,
      sorter: (a, b) => a.total_amount - b.total_amount,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{statusLabels[s] || s}</Tag>,
    },
    {
      title: 'Sana',
      key: 'date',
      render: (_, r) => `${r.day || '-'}/${r.month || '-'}/${r.year || '-'}`,
    },
    {
      title: 'Yaratilgan',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EyeOutlined />} onClick={() => { setDetailData(record); setDetailOpen(true); }} />
          <Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filtered = bookings?.filter((b) => {
    const matchSearch = !search || String(b.id).includes(search) || String(b.service_id).includes(search);
    const matchStatus = !statusFilter || b.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = useMemo(() => {
    const list = bookings || [];
    return {
      total: list.length,
      pending: list.filter(b => b.status === 'pending').length,
      completed: list.filter(b => b.status === 'completed').length,
      totalAmount: list.reduce((s, b) => s + Number(b.total_amount || 0), 0),
    };
  }, [bookings]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Title level={3} style={{ margin: 0 }}>Buyurtmalar</Title>
        <Space wrap>
          <Input placeholder="ID bo'yicha qidirish..." prefix={<SearchOutlined />} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} allowClear />
          <Select
            placeholder="Holat"
            allowClear
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 160 }}
            options={Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchBookings()}>Yangilash</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami buyurtmalar" value={stats.total} icon={<CalendarOutlined />} color="#4f46e5" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Kutilmoqda" value={stats.pending} icon={<ClockCircleOutlined />} color="#fa8c16" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Yakunlangan" value={stats.completed} icon={<CheckCircleOutlined />} color="#52c41a" loading={loading} />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard title="Jami summa" value={stats.totalAmount} suffix="UZS" icon={<DollarOutlined />} color="#13c2c2" loading={loading} />
        </Col>
      </Row>

      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
          onChange={(pg) => fetchBookings({ page: pg.current, per_page: pg.pageSize })}
        />
      </Card>

      <Modal title="Buyurtma tafsilotlari" open={detailOpen} onCancel={() => setDetailOpen(false)} footer={null} width={600}>
        {detailData && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div><strong>ID:</strong> {detailData.id}</div>
            <div><strong>Xizmat ID:</strong> {detailData.service_id}</div>
            <div><strong>Foydalanuvchi ID:</strong> {detailData.user_id || '-'}</div>
            <div><strong>Provayder ID:</strong> {detailData.provider_id || '-'}</div>
            <div><strong>Sana:</strong> {detailData.day}/{detailData.month}/{detailData.year}</div>
            <div><strong>Vaqt:</strong> {detailData.start_datetime} - {detailData.end_datetime}</div>
            <div><strong>Soni:</strong> {detailData.quantity}</div>
            <div><strong>Narxi:</strong> {Number(detailData.price || 0).toLocaleString()} UZS</div>
            <div><strong>Jami summa:</strong> {Number(detailData.total_amount || 0).toLocaleString()} UZS</div>
            <div><strong>Holat:</strong> <Tag color={statusColors[detailData.status]}>{statusLabels[detailData.status] || detailData.status}</Tag></div>
            <div><strong>Yaratilgan:</strong> {detailData.created_at ? new Date(detailData.created_at).toLocaleString('uz-UZ') : '-'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BookingsPage;
